import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await authAPI.getMe();
      setUser(data);
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email, password,captcha_token) => {
    const { data } = await authAPI.login({ email, password, captcha_token });
    if (data.requires_2fa) {
      return data; // Return to caller to handle 2FA flow
    }
    localStorage.setItem('token', data.token);
    setUser(data.user);
    return data;
  };

  const setAuth = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const register = async (formData) => {
    const { data } = await authAPI.register(formData);
    // Don't set user immediately — let the caller (RegisterPage) show the
    // success modal first, then call completeRegistration to set user state.
    localStorage.setItem('token', data.token);
    return data;
  };

  const completeRegistration = (userData) => {
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/';
  };

  const refreshUser = async () => {
    try {
      const { data } = await authAPI.getMe();
      setUser(data);
    } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, completeRegistration, logout, refreshUser, setAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
